import useScrollAnimation from "@/hooks/useScrollAnimation";
import { enterpriseTrustData } from "@/mocks/homeData";

export default function EnterpriseTrustSection() {
  const { ref, isVisible } = useScrollAnimation(0.08);

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="enterprise-trust" className="w-full py-16 md:py-24 bg-foreground-950">
      <div
        ref={ref}
        className={`w-full px-4 md:px-6 lg:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <span className="text-xs md:text-sm font-semibold text-accent-500 uppercase tracking-widest">
            {enterpriseTrustData.label}
          </span>
          <h2 className="font-heading font-bold text-xl md:text-3xl lg:text-4xl text-background-50 mt-3 mb-4 leading-tight">
            {enterpriseTrustData.headline}
          </h2>
          <p className="text-sm md:text-base text-background-300 leading-relaxed max-w-2xl mx-auto">
            {enterpriseTrustData.subheadline}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto">
          {enterpriseTrustData.sections.map((section) => (
            <div
              key={section.title}
              className="group bg-foreground-900/40 rounded-xl p-5 md:p-6 border border-foreground-800/50 hover:border-accent-500/30 transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-accent-500/12 flex items-center justify-center shrink-0">
                  <i className={`${section.icon} text-base md:text-lg text-accent-400`} />
                </div>
                <h3 className="font-heading font-semibold text-sm md:text-base text-background-50">
                  {section.title}
                </h3>
              </div>

              <div className="space-y-3">
                {section.items.map((item) => (
                  <div key={item.label} className="flex items-start gap-2.5">
                    <i className="ri-check-line text-accent-500 text-sm mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <span className="text-xs md:text-sm font-semibold text-background-200 block leading-snug">
                        {item.label}
                      </span>
                      <span className="text-xs text-background-400 leading-relaxed block mt-0.5">
                        {item.detail}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-4xl mx-auto bg-foreground-900/30 rounded-xl p-5 md:p-7 border border-foreground-800/40 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div>
            <h4 className="font-heading font-semibold text-sm md:text-base text-background-50 mb-1.5">
              Ready to initiate a vendor assessment?
            </h4>
            <p className="text-xs md:text-sm text-background-400 leading-relaxed max-w-lg">
              We can complete your security questionnaire and provide insurance certificates, DPA, and reference contacts within five business days. No sales pitch required — just the documentation your procurement team needs.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => handleNavClick("#contact")}
              className="px-6 py-3 text-sm font-semibold rounded-md bg-accent-500 text-foreground-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              Submit RFI / RFP
            </button>
            <a
              href="mailto:security@redeemsoft.com"
              className="px-6 py-3 text-sm font-semibold rounded-md border border-background-300/30 text-background-200 hover:bg-background-50/8 transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              Contact Security Team
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}